import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HeadphonesIcon,
  Ticket,
  Phone,
  Users,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Loader2,
  RefreshCw,
  MessageSquare,
} from 'lucide-react';
import { supportService } from '../services/support';
import {
  Ticket as TicketType,
  TicketStatus,
  TicketPriority,
  EscalationStatus,
} from '../types/support';
import {
  formatRelativeTime,
  getStatusLabel,
  getStatusBadgeClass,
  getPriorityBadgeClass,
  getPriorityLabel,
  getUserDisplayName,
} from '../utils/helpers';
import Avatar from '../components/ui/Avatar';
import Spinner from '../components/ui/Spinner';

interface DashboardStats {
  openTickets: number;
  inProgressTickets: number;
  resolvedToday: number;
  escalatedTickets: number;
  urgentTickets: number;
  totalTickets: number;
}

const TicketsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    openTickets: 0,
    inProgressTickets: 0,
    resolvedToday: 0,
    escalatedTickets: 0,
    urgentTickets: 0,
    totalTickets: 0,
  });
  const [recentTickets, setRecentTickets] = useState<TicketType[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch dashboard data
  const fetchDashboardData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Fetch tickets with different filters in parallel
      const [
        allTicketsRes,
        openTicketsRes,
        inProgressRes,
        escalatedRes,
        urgentRes,
      ] = await Promise.all([
        supportService.getTickets({ page: 1, pageSize: 10 }),
        supportService.getTickets({ status: TicketStatus.OPEN, page: 1, pageSize: 1 }),
        supportService.getTickets({ status: TicketStatus.IN_PROGRESS, page: 1, pageSize: 1 }),
        supportService.getTickets({ escalationStatus: EscalationStatus.AGENT_REQUESTED_ADMIN_REVIEW, page: 1, pageSize: 1 }),
        supportService.getTickets({ priority: TicketPriority.URGENT, page: 1, pageSize: 1 }),
      ]);

      // Calculate resolved today (tickets resolved in the last 24 hours)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const resolvedTodayCount = allTicketsRes.data.filter(
        (ticket) =>
          ticket.status === TicketStatus.RESOLVED &&
          new Date(ticket.updatedAt) >= today
      ).length;

      setStats({
        openTickets: openTicketsRes.total,
        inProgressTickets: inProgressRes.total,
        resolvedToday: resolvedTodayCount,
        escalatedTickets: escalatedRes.total,
        urgentTickets: urgentRes.total,
        totalTickets: allTicketsRes.total,
      });

      // Set recent tickets for activity feed
      setRecentTickets(allTicketsRes.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Stats cards configuration
  const statCards = useMemo(
    () => [
      {
        label: 'Open Tickets',
        value: stats.openTickets,
        icon: Ticket,
        color: 'text-primary',
        bg: 'bg-primary/10',
        link: '/support?status=OPEN',
      },
      {
        label: 'In Progress',
        value: stats.inProgressTickets,
        icon: Clock,
        color: 'text-blue-400',
        bg: 'bg-blue-500/10',
        link: '/support?status=IN_PROGRESS',
      },
      {
        label: 'Escalated',
        value: stats.escalatedTickets,
        icon: AlertTriangle,
        color: 'text-destructive',
        bg: 'bg-destructive/10',
        link: '/support?escalationStatus=AGENT_REQUESTED_ADMIN_REVIEW',
      },
      {
        label: 'Urgent',
        value: stats.urgentTickets,
        icon: AlertTriangle,
        color: 'text-warning',
        bg: 'bg-warning/10',
        link: '/support?priority=URGENT',
      },
    ],
    [stats]
  );

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="text-muted-foreground mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-2">
            Failed to load dashboard
          </h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => fetchDashboardData()}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Tickets
          </h1>
          <p className="text-muted-foreground mt-1">
            {stats.totalTickets} total tickets •{' '}
            {stats.openTickets + stats.inProgressTickets} active
          </p>
        </div>
        <button
          onClick={() => fetchDashboardData(true)}
          disabled={refreshing}
          className="btn btn-ghost"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.label}
              to={stat.link}
              className="card p-5 hover:border-primary/30 transition-all group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${stat.bg}`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-2xl font-bold text-foreground">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/support"
            className="card p-6 hover:border-primary/30 hover:shadow-glow-sm transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <HeadphonesIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">
                  Customer Support
                </h3>
                <p className="text-sm text-muted-foreground">
                  View and manage tickets
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/calls"
            className="card p-6 hover:border-primary/30 hover:shadow-glow-sm transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-success/10 group-hover:bg-success/20 transition-colors">
                <Phone className="h-6 w-6 text-success" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Call Center</h3>
                <p className="text-sm text-muted-foreground">
                  Manage voice calls
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/users"
            className="card p-6 hover:border-primary/30 hover:shadow-glow-sm transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-warning/10 group-hover:bg-warning/20 transition-colors">
                <Users className="h-6 w-6 text-warning" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Users</h3>
                <p className="text-sm text-muted-foreground">
                  Manage user accounts
                </p>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Tickets */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Recent Tickets
          </h2>
          <Link
            to="/support"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="card overflow-hidden">
          {recentTickets.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm">No tickets yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentTickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => navigate(`/support?ticket=${ticket.id}`)}
                  className="w-full text-left p-4 hover:bg-primary/5 transition-colors flex items-center gap-4"
                >
                  <Avatar
                    name={getUserDisplayName(ticket.user, ticket.userId)}
                    src={ticket.user?.profileImage}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {ticket.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`badge text-[10px] ${getStatusBadgeClass(
                          ticket.status
                        )}`}
                      >
                        {getStatusLabel(ticket.status)}
                      </span>
                      <span
                        className={`badge text-[10px] ${getPriorityBadgeClass(
                          ticket.priority
                        )}`}
                      >
                        {getPriorityLabel(ticket.priority)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {getUserDisplayName(ticket.user, ticket.userId)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-muted-foreground">
                      {formatRelativeTime(ticket.updatedAt)}
                    </p>
                    {ticket._count && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {ticket._count.messages} messages
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketsDashboard;
